import React, { useState, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import AuthService from '../services/auth';
import { Box, CircularProgress, Typography } from '@mui/material';

const ProtectedRoute = ({ adminOnly = false, superAdminOnly = false }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      try {
        setIsLoading(true);

        // First check authentication
        const authStatus = await AuthService.isAuthenticated();

        if (!isMounted) return;
        setIsAuthenticated(authStatus);

        // If not authenticated, redirect immediately
        if (!authStatus) {
          if (isMounted) {
            setIsLoading(false);
          }
          return;
        }

        // If authenticated and admin route, check admin status
        if (adminOnly) {
          try {
            const adminStatus = await AuthService.isAdmin();
            if (!isMounted) return;
            setIsAdmin(adminStatus);
          } catch (error) {
            console.error('Admin check error:', error);
            if (!isMounted) return;
            setIsAdmin(false);
          }

          // Also check superadmin status for admin routes
          try {
            const superAdminStatus = await AuthService.isSuperAdmin();
            if (!isMounted) return;
            setIsSuperAdmin(superAdminStatus);
          } catch (error) {
            console.error('SuperAdmin check error:', error);
            if (!isMounted) return;
            setIsSuperAdmin(false);
          }
        }

        // If authenticated and superadmin route, check superadmin status
        if (superAdminOnly && !adminOnly) { // Only check if not already checked above
          try {
            const superAdminStatus = await AuthService.isSuperAdmin();
            if (!isMounted) return;
            setIsSuperAdmin(superAdminStatus);
          } catch (error) {
            console.error('SuperAdmin check error:', error);
            if (!isMounted) return;
            setIsSuperAdmin(false);
          }
        }
      } catch (error) {
        console.error('Authentication check error:', error);
        if (!isMounted) return;
        setIsAuthenticated(false);

        // Force redirect to login on auth error
        if (isMounted) {
          setIsLoading(false);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    checkAuth();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [adminOnly, superAdminOnly]);

  // Show loading while checking authentication
  if (isLoading || isAuthenticated === null || (adminOnly && isAdmin === null) || (superAdminOnly && isSuperAdmin === null)) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Перевірка авторизації...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin && !isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  if (superAdminOnly && !isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
